import html2pdf from 'html2pdf.js';

export interface PdfExportOptions {
  container: HTMLElement;
  filename: string;
  gridTemplateColumns: string;
  headerSelector: string;
  rowSelector: string;
  wrapperSelector: string;
  contentContainerSelector: string;
  removeSelectors?: string[];
  prepareClone?: (clone: HTMLElement) => void;
  filterTags?: string[];
  pageInfoText?: string;
  additionalContainerClasses?: string[];
  additionalContent?: (pdfContainer: HTMLElement) => void;
  onCloneAdjust?: (clonedDoc: Document, containerHeightWithPadding: number) => void;
  margins?: [number, number, number, number];
  delayMs?: number;
}

const PDF_CONTENT_WIDTH = 718; // Matches A4 printable area at 96dpi with 10mm margins

const appendFilterTags = (pdfContainer: HTMLElement, tags: string[]) => {
  if (!tags.length) return;
  const filterSection = document.createElement('div');
  filterSection.className = 'pdf-filters';
  tags.forEach(tag => {
    const span = document.createElement('span');
    span.textContent = tag;
    span.className = 'pdf-filter-tag';
    filterSection.appendChild(span);
  });
  pdfContainer.appendChild(filterSection);
};

const appendPageInfo = (pdfContainer: HTMLElement, text: string) => {
  if (!text) return;
  const pageInfo = document.createElement('div');
  pageInfo.className = 'pdf-page-info';
  pageInfo.textContent = text;
  pdfContainer.appendChild(pageInfo);
};

export const exportTableAsPdf = async ({
  container,
  filename,
  gridTemplateColumns,
  headerSelector,
  rowSelector,
  wrapperSelector,
  contentContainerSelector,
  removeSelectors = [],
  prepareClone,
  filterTags = [],
  pageInfoText,
  additionalContainerClasses = [],
  additionalContent,
  onCloneAdjust,
  margins = [0, 10, 10, 10],
  delayMs = 500
}: PdfExportOptions) => {
  if (!container) return;

  const pdfContainer = document.createElement('div');
  pdfContainer.className = 'pdf-export-container';
  additionalContainerClasses.forEach(cls => pdfContainer.classList.add(cls));

  if (filterTags.length) appendFilterTags(pdfContainer, filterTags);
  if (pageInfoText) appendPageInfo(pdfContainer, pageInfoText);
  if (additionalContent) additionalContent(pdfContainer);

  const tableClone = container.cloneNode(true) as HTMLElement;

  removeSelectors.forEach(selector => {
    tableClone.querySelectorAll(selector).forEach(node => node.remove());
  });

  if (prepareClone) {
    prepareClone(tableClone);
  }

  pdfContainer.appendChild(tableClone);
  document.body.appendChild(pdfContainer);

  // Force reflow so styles compute before manipulation
  void pdfContainer.offsetHeight;

  const applyGridTemplate = () => {
    const header = tableClone.querySelector<HTMLElement>(headerSelector);
    if (header) {
      header.style.gridTemplateColumns = gridTemplateColumns;
      void header.offsetWidth;
    }
    const rows = tableClone.querySelectorAll<HTMLElement>(rowSelector);
    rows.forEach(row => {
      row.style.gridTemplateColumns = gridTemplateColumns;
      void row.offsetWidth;
    });
  };

  applyGridTemplate();

  const waitForStylesheets = () => {
    const links = pdfContainer.querySelectorAll('link[rel="stylesheet"]');
    if (!links.length) return Promise.resolve();
    return Promise.all(
      Array.from(links).map(link => new Promise<void>((resolve) => {
        const linkEl = link as HTMLLinkElement;
        if (linkEl.sheet || linkEl.href === '') {
          resolve();
          return;
        }
        linkEl.onload = () => resolve();
        linkEl.onerror = () => resolve();
        setTimeout(() => resolve(), 1000);
      }))
    );
  };

  await waitForStylesheets();

  void pdfContainer.offsetHeight;
  applyGridTemplate();

  await new Promise(resolve => setTimeout(resolve, delayMs));

  if (!pdfContainer.parentNode) {
    return;
  }

  const contentContainers = pdfContainer.querySelectorAll<HTMLElement>(contentContainerSelector);
  contentContainers.forEach(content => {
    content.style.width = '100%';
    content.style.maxWidth = '100%';
    content.style.margin = '0';
    void content.offsetWidth;
  });

  void pdfContainer.offsetWidth;

  const measureFullHeight = () => {
    void pdfContainer.offsetHeight;
    void pdfContainer.scrollHeight;

    const containerWidth = pdfContainer.offsetWidth || PDF_CONTENT_WIDTH;
    const contentContainer = pdfContainer.querySelector<HTMLElement>(contentContainerSelector);
    const contentHeight = contentContainer ? contentContainer.scrollHeight : 0;
    const containerHeight = Math.max(
      pdfContainer.scrollHeight,
      pdfContainer.offsetHeight,
      contentHeight
    );

    const wrappers = pdfContainer.querySelectorAll<HTMLElement>(wrapperSelector);
    let lastRowHeight = 0;
    if (wrappers.length > 0) {
      const lastRow = wrappers[wrappers.length - 1];
      lastRowHeight = lastRow.scrollHeight || lastRow.offsetHeight || 0;
    }

    const containerHeightWithPadding = containerHeight + lastRowHeight + 20;

    return { containerWidth, containerHeightWithPadding };
  };

  const { containerWidth, containerHeightWithPadding } = measureFullHeight();

  const cleanup = () => {
    if (pdfContainer.parentNode) {
      document.body.removeChild(pdfContainer);
    }
  };

  const options = {
    margin: margins as [number, number, number, number],
    filename,
    image: { type: 'jpeg' as const, quality: 0.98 },
    pagebreak: { mode: ['css'] as const },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: containerWidth,
      height: containerHeightWithPadding,
      windowWidth: containerWidth,
      windowHeight: containerHeightWithPadding,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
      removeContainer: false,
      allowTaint: false,
      foreignObjectRendering: false,
      imageTimeout: 0,
      onclone: (clonedDoc: Document) => {
        const clonedContainer = clonedDoc.querySelector('.pdf-export-container') as HTMLElement | null;
        if (clonedContainer) {
          clonedContainer.style.maxHeight = 'none';
          clonedContainer.style.height = 'auto';
          clonedContainer.style.overflow = 'visible';
          clonedContainer.style.overflowY = 'visible';
          clonedContainer.style.minHeight = `${containerHeightWithPadding}px`;
        }
        const clonedContent = clonedDoc.querySelector(contentContainerSelector) as HTMLElement | null;
        if (clonedContent) {
          clonedContent.style.maxHeight = 'none';
          clonedContent.style.height = 'auto';
          clonedContent.style.overflow = 'visible';
        }
        if (onCloneAdjust) {
          onCloneAdjust(clonedDoc, containerHeightWithPadding);
        }
      }
    },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
  };

  try {
    await html2pdf().set(options).from(pdfContainer).save();
  } catch (error) {
    console.error('Error generating PDF:', error);
  } finally {
    cleanup();
  }
};


